/**
 * Client-side mirror of the address identity rules in `libs/services/src/order-reassignment.ts`.
 *
 * These exist so the delivery edit sheet can tell the admin what saving will actually do -
 * reuse a saved address, or save a new one - before they commit. The server stays the source
 * of truth; this only drives the confirmation copy.
 *
 * Line 2 is part of the identity on purpose: `100 Main St Apt 2` and `100 Main St Apt 5` are
 * different service addresses even though Google resolves them to the same place id.
 */

export interface MatchableAddress {
  _id?: string;
  line1?: string;
  line2?: string | null;
  city?: string;
  state?: string;
  postalCode?: string;
  googlePlacesId?: string;
}

const normalize = (value?: string | null): string => (value || '').trim().toLowerCase();

// `libs/schemas/src/address.ts` accepts ZIP+4 in both `33101-1234` and `33101 1234` form, and
// the server's `normalizePostalCode` strips either separator. Mirror that exactly - stripping
// only the dash here would drift from the server and mislabel a space-form ZIP+4 as a new
// address when the server would actually reuse the existing record.
const normalizePostalCode = (value?: string | null): string =>
  normalize(value).split(/[-\s]/)[0] || '';

const identityKey = (address: MatchableAddress): string =>
  [
    normalize(address.line1),
    normalize(address.line2),
    normalize(address.city),
    normalize(address.state),
    normalizePostalCode(address.postalCode),
  ].join('|');

const sameLine2 = (a?: string | null, b?: string | null): boolean => normalize(a) === normalize(b);

export const formatAddressLabel = (address?: MatchableAddress | null): string => {
  if (!address?.line1) {
    return 'No address on this order yet';
  }
  const street = [address.line1, address.line2].filter(Boolean).join(' ');
  return [street, address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
};

/**
 * Find the saved address a proposed address would resolve to.
 *
 * The printed fields decide identity on their own: line 1, line 2, city, state and postal code
 * all matching means the saved record is reused, whether or not a place id is present or agrees.
 * A difference in any of them is a different service address and gets its own record. Place id
 * is only a fallback for a proposal that has no line 1 to compare yet.
 */
export const findMatchingSavedAddress = (
  proposed: MatchableAddress | null | undefined,
  savedAddresses: MatchableAddress[] | undefined,
): MatchableAddress | null => {
  if (!proposed || !savedAddresses?.length) {
    return null;
  }

  if (proposed.line1) {
    const key = identityKey(proposed);
    return savedAddresses.find((address) => identityKey(address) === key) || null;
  }

  if (proposed.googlePlacesId) {
    return (
      savedAddresses.find(
        (address) =>
          address.googlePlacesId === proposed.googlePlacesId &&
          sameLine2(address.line2, proposed.line2),
      ) || null
    );
  }

  return null;
};

/**
 * True when the street matches a saved address but the unit does not - the case where a naive
 * in-place edit would overwrite the unit on an address other orders also point at.
 */
export const isNewUnitOnKnownStreet = (
  proposed: MatchableAddress | null | undefined,
  savedAddresses: MatchableAddress[] | undefined,
): boolean => {
  if (!proposed || !savedAddresses?.length || findMatchingSavedAddress(proposed, savedAddresses)) {
    return false;
  }

  return savedAddresses.some(
    (address) =>
      (proposed.googlePlacesId
        ? address.googlePlacesId === proposed.googlePlacesId
        : normalize(address.line1) === normalize(proposed.line1) &&
          normalizePostalCode(address.postalCode) === normalizePostalCode(proposed.postalCode)) &&
      !sameLine2(address.line2, proposed.line2),
  );
};

/**
 * Whether the proposed address differs from the one currently on the order. The delivery edit
 * sheet's address form fires a change event on mount, so a value-level comparison - not the
 * mere presence of form state - is what decides if a correction needs to run.
 */
export const isAddressChanged = (
  proposed: MatchableAddress | null | undefined,
  current: MatchableAddress | null | undefined,
): boolean => {
  if (!proposed?.line1) {
    return false;
  }
  if (!current?.line1) {
    return true;
  }
  return identityKey(proposed) !== identityKey(current);
};
